/* eslint-disable confluence-feature-gating/no-exposure-recommendation */
import React, { useState, useCallback, forwardRef } from 'react';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import type { PopupComponentProps } from '@atlaskit/popup/types';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer -- Requested suppression for existing Popup import
import { Popup } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	manuallyLogExpExposure,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';

import { useMultiHoverPopupCard } from '../../hooks/useMultiHoverPopupCard.tsx';
import type { PageCardContextProviderProps } from '../../PageCardContextProvider.tsx';
import { PageCardContextProvider } from '../../PageCardContextProvider.tsx';
import { PopupPageCardViewWithAISnippet } from '../../Views/PopupPageCardViewWithAISnippet.tsx';

const pageRecCardContainer = xcss({
	height: '100%',
});

const popupCardStyles = xcss({
	borderRadius: 'radius.large',
});

export type AISnippetCardExperimentProps = {
	children: (
		setIsHoveringInfoIcon?: (isHoveringInfoIcon: boolean) => void,
		onMouseLeaveIcon?: () => void,
		onMouseEnterIcon?: () => void,
	) => React.JSX.Element;
	wrapperProps: Omit<PageCardContextProviderProps, 'appearance'>;
	isCompactView?: boolean;
};

export const AISnippetPageRecExperiment = ({
	children,
	wrapperProps,
	isCompactView,
}: AISnippetCardExperimentProps): React.JSX.Element => {
	const { isAdminHubAIEnabled } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isOpen, onMouseEnter, onMouseLeave, onMouseEnterIcon, onMouseLeaveIcon } =
		useMultiHoverPopupCard();
	const [isHoveringInfoIcon, setIsHoveringInfoIcon] = useState(false);
	const { id, type } = wrapperProps.data;

	const onMouseEnterWrapper = useCallback(() => {
		if (isAdminHubAIEnabled) {
			manuallyLogExpExposure('confluence_ai_enabled_related_pages_snippets');
		} else {
			manuallyLogExpExposure('confluence_ai_disabled_related_pages_snippets');
		}

		if (isHoveringInfoIcon) {
			return;
		}

		onMouseEnter();
	}, [isAdminHubAIEnabled, onMouseEnter, isHoveringInfoIcon]);

	const onMouseLeaveWrapper = useCallback(() => {
		if (isHoveringInfoIcon) {
			return;
		}
		onMouseLeave();
	}, [isHoveringInfoIcon, onMouseLeave]);

	const fireAnalyticsEvent = useCallback(
		(action: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'globalPageCard',
					source: 'endOfPageRecommendations',
					attributes: {
						contentType: type,
						contentId: id,
						from: FeatureCode.EOP_REC_CARD,
						view: 'end-of-page-rec-popup-view',
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, id, type],
	);

	const triggerContent = useCallback(
		(triggerProps) => (
			<Box
				xcss={pageRecCardContainer}
				{...triggerProps}
				onMouseEnter={onMouseEnterWrapper}
				onMouseLeave={onMouseLeaveWrapper}
			>
				<PageCardContextProvider
					{...wrapperProps}
					appearance={isCompactView ? 'compact-end-of-page' : 'end-of-page'}
				>
					{children(setIsHoveringInfoIcon, onMouseLeaveIcon, onMouseEnterIcon)}
				</PageCardContextProvider>
			</Box>
		),
		[
			children,
			onMouseEnterWrapper,
			onMouseLeaveWrapper,
			onMouseLeaveIcon,
			onMouseEnterIcon,
			wrapperProps,
			isCompactView,
		],
	);

	const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>(
		(
			{
				'data-testid': testId,
				shouldRenderToParent: _shouldRenderToParent,
				shouldFitContainer: _shouldFitContainer,
				shouldFitViewport: _shouldFitViewport,
				isReferenceHidden: _isReferenceHidden,
				...props
			},
			ref,
		) => (
			<Box
				testId={testId}
				{...props}
				ref={ref}
				xcss={popupCardStyles}
				onMouseEnter={() => fireAnalyticsEvent('hovered')}
				onClickCapture={() => fireAnalyticsEvent('clicked')}
			/>
		),
	);

	const isInAIEnabledSnippetsExp = isAdminHubAIEnabled && type !== 'blogpost';

	const isAISnippetHoverEnabled =
		isInAIEnabledSnippetsExp &&
		UNSAFE_expValEqualsNoExposure(
			'confluence_ai_enabled_related_pages_snippets',
			'cohort',
			'show_ai_snippet',
		);
	const isAIWithExcerptExp =
		isInAIEnabledSnippetsExp &&
		UNSAFE_expValEqualsNoExposure(
			'confluence_ai_enabled_related_pages_snippets',
			'cohort',
			'show_snippet',
		);

	const isNonAIHoverEnabled =
		!isInAIEnabledSnippetsExp &&
		UNSAFE_expValEqualsNoExposure(
			'confluence_ai_disabled_related_pages_snippets',
			'cohort',
			'show_snippet',
		);

	if (isAISnippetHoverEnabled || isAIWithExcerptExp || isNonAIHoverEnabled) {
		return (
			<Popup
				popupComponent={PopupComponent}
				trigger={(triggerProps) => triggerContent(triggerProps)}
				isOpen={isOpen}
				// Given the visibility of this component is handled via mouse hover events, restoring focus could result in unpredictable behaviour.
				// autoFocus=false is used for the same reason HoverCardComponent sets this to false.
				autoFocus={false}
				content={(props) => (
					<PopupPageCardViewWithAISnippet
						id={id}
						type={type}
						showAISnippets={isAISnippetHoverEnabled}
						onMouseEnter={onMouseEnterWrapper}
						onMouseLeave={onMouseLeaveWrapper}
						update={props.update}
					/>
				)}
				placement="top-start"
			/>
		);
	}

	return (
		<Box
			xcss={pageRecCardContainer}
			onMouseEnter={onMouseEnterWrapper}
			onMouseLeave={onMouseLeaveWrapper}
		>
			<PageCardContextProvider {...wrapperProps} appearance="end-of-page">
				{children()}
			</PageCardContextProvider>
		</Box>
	);
};
